import { useState, useEffect } from 'react';
import { marketplaceService } from '../../_services/marketplace.service';
import { productService } from '../../_services/product.service';
import { mapProducts } from './productShopMapper';

// useProductShopData — loads everything the customer product shop needs for a
// salon: its public identity (name, currency, logo) and the product catalogue.
//
// The catalogue comes from the PUBLIC store endpoint
// (GET product/store/public/:salonId/products — no user token), so signed-out
// customers see the real online range. It returns nothing unless the salon's
// store is PUBLISHED, which doubles as the salon-wide online master switch —
// the localStorage channels stand-in is no longer consulted here.

const useProductShopData = (salonIdOrSlug) => {
  const [state, setState] = useState({
    loading: true,
    salonId: salonIdOrSlug,
    salonName: 'Your Salon',
    currency: '£',
    salonLogo: null,
    products: [],
    categories: ['Featured'],
  });

  useEffect(() => {
    let alive = true;
    const run = async () => {
      let salonName = 'Your Salon';
      let currency = '£';
      let salonLogo = null;
      let realSalonId = salonIdOrSlug;

      // Salon identity (and the real salon id for the product fetch).
      try {
        const resp = await marketplaceService.getSalonSummary({ salonId: salonIdOrSlug });
        const salon = resp?.salon || resp;
        if (salon) {
          salonName = salon.salonName || salon.name || salonName;
          currency = salon.currencySymbol || salon.currency || currency;
          salonLogo = salon.image || salon.logo || null;
          realSalonId = salon._id || salon.id || realSalonId;
        }
      } catch (e) {
        console.log('[ProductShop] salon summary unavailable, using defaults:', e?.message);
      }

      // Catalogue — public endpoint, published stores only. Empty result (store
      // offline / no online products) → the shop and its preview hide themselves.
      let products = [];
      try {
        // The endpoint takes the salon's id. If the summary lookup failed we
        // may still be holding the URL slug — that request can never succeed,
        // so don't fire it (the shop hides itself, same as store-offline).
        if (!/^[0-9a-f]{24}$/i.test(String(realSalonId))) throw new Error('salon id unresolved');
        const pub = await productService.getPublicProducts(realSalonId);
        if (pub && pub.published) {
          products = mapProducts(pub.products || []);
        }
      } catch (e) {
        console.log('[ProductShop] public product list unavailable:', e?.message);
      }

      const categories = ['Featured', ...Array.from(new Set(products.map((p) => p.category).filter(Boolean)))];

      if (alive) setState({ loading: false, salonId: realSalonId, salonName, currency, salonLogo, products, categories });
    };
    run();
    return () => { alive = false; };
  }, [salonIdOrSlug]);

  return state;
};

export default useProductShopData;
